const MESES_CORTOS = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

export const MESES_NOMBRES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

export function formatMesAnioLabel(mes, anio) {
  return `${MESES_CORTOS[mes]} ${anio}`;
}

export function formatMesAnioLargo(mes, anio) {
  return `${MESES_NOMBRES[mes]} ${anio}`;
}

export function isMesActual(mes, anio) {
  const hoy = new Date();
  return mes === hoy.getMonth() && anio === hoy.getFullYear();
}

export function getMesesComparacion(mes, anio) {
  const result = [];
  for (let offset = -2; offset <= 0; offset += 1) {
    const d = new Date(anio, mes + offset, 1);
    result.push({ mes: d.getMonth(), anio: d.getFullYear() });
  }
  return result;
}

export function filterVentasPorMes(ventas, mes, anio) {
  return (ventas || []).filter((v) => {
    const d = new Date(v.fecha);
    return d.getMonth() === mes && d.getFullYear() === anio;
  });
}

export function sumTotalVentas(ventas) {
  return (ventas || []).reduce((acc, v) => acc + (parseFloat(v.total) || 0), 0);
}

export function getAniosDisponibles(ventas) {
  const anioActual = new Date().getFullYear();
  let minAnio = anioActual;

  (ventas || []).forEach((v) => {
    if (!v?.fecha) return;
    const anio = new Date(v.fecha).getFullYear();
    if (anio < minAnio) minAnio = anio;
  });

  const anios = [];
  for (let a = anioActual; a >= minAnio; a -= 1) {
    anios.push(a);
  }
  return anios;
}

export function buildTopProductosMes(ventas, detalleVentas, mes, anio) {
  const ventasMes = filterVentasPorMes(ventas, mes, anio);
  const ventasMap = {};
  ventasMes.forEach((v) => {
    ventasMap[v.id] = v;
  });

  const ventaIds = new Set(ventasMes.map((v) => v.id));
  const detallesMes = (detalleVentas || []).filter((d) => ventaIds.has(d.venta_id));

  const brutoPorVenta = {};
  detallesMes.forEach((d) => {
    const lineaBruta = (parseInt(d.cantidad, 10) || 0) * (parseFloat(d.precio_unitario) || 0);
    brutoPorVenta[d.venta_id] = (brutoPorVenta[d.venta_id] || 0) + lineaBruta;
  });

  const map = {};
  detallesMes.forEach((d) => {
    if (!map[d.producto_id]) {
      map[d.producto_id] = { nombre: d.nombre, cantidad: 0, total: 0 };
    }

    const qty = parseInt(d.cantidad, 10) || 0;
    const precio = parseFloat(d.precio_unitario) || 0;
    const lineaBruta = qty * precio;
    const venta = ventasMap[d.venta_id];
    const descuentoVenta = Math.max(0, parseFloat(venta?.descuento_total) || 0);
    const brutoVenta = Math.max(0, parseFloat(brutoPorVenta[d.venta_id]) || 0);
    const proporcion = brutoVenta > 0 ? lineaBruta / brutoVenta : 0;
    const descuentoLinea = descuentoVenta * proporcion;
    const lineaNeta = Math.max(0, lineaBruta - descuentoLinea);

    map[d.producto_id].cantidad += qty;
    map[d.producto_id].total += lineaNeta;
  });

  return Object.entries(map)
    .map(([id, data]) => ({ id, ...data }))
    .sort((a, b) => b.cantidad - a.cantidad);
}

export function calcGananciaMes(ventas, detalleVentas, mes, anio, obtenerCostoPromedioProducto) {
  const ventasMes = filterVentasPorMes(ventas, mes, anio);

  let ingreso = 0;
  let costo = 0;

  ventasMes.forEach((v) => {
    const detallesVenta = (detalleVentas || []).filter((d) => d.venta_id === v.id);

    const subtotalDetalle = detallesVenta.reduce(
      (acc, d) => acc + (parseInt(d.cantidad, 10) || 0) * (parseFloat(d.precio_unitario) || 0),
      0
    );

    const descuentoVenta = Math.max(0, parseFloat(v.descuento_total) || 0);
    const totalVenta = parseFloat(v.total);
    const ingresoNetoVenta = Number.isFinite(totalVenta)
      ? totalVenta
      : Math.max(0, subtotalDetalle - descuentoVenta);

    ingreso += ingresoNetoVenta;

    detallesVenta.forEach((d) => {
      const qty = parseInt(d.cantidad, 10) || 0;
      const costoUnitarioDetalle = parseFloat(d.costo_unitario) || 0;
      const costoPromedio = obtenerCostoPromedioProducto
        ? obtenerCostoPromedioProducto(d.producto_id)
        : 0;
      const costoUnitario = costoUnitarioDetalle > 0 ? costoUnitarioDetalle : costoPromedio;
      costo += qty * costoUnitario;
    });
  });

  return ingreso - costo;
}

export function getDiasEnMes(mes, anio) {
  return new Date(anio, mes + 1, 0).getDate();
}

export function buildAcumuladoDiarioMes(ventas, mes, anio) {
  const ventasMes = filterVentasPorMes(ventas, mes, anio);
  const diasEnMes = getDiasEnMes(mes, anio);

  const totalesPorDia = {};
  ventasMes.forEach((v) => {
    const dia = new Date(v.fecha).getDate();
    totalesPorDia[dia] = (totalesPorDia[dia] || 0) + (parseFloat(v.total) || 0);
  });

  let acumulado = 0;
  const result = [];
  for (let dia = 1; dia <= diasEnMes; dia += 1) {
    acumulado += totalesPorDia[dia] || 0;
    result.push({ dia, acumulado });
  }
  return result;
}

export function buildComparacionMensualChartData(ventas, mes, anio) {
  const meses = getMesesComparacion(mes, anio);
  const maxDias = Math.max(...meses.map((m) => getDiasEnMes(m.mes, m.anio)));

  const seriesData = meses.map((m) => ({
    label: formatMesAnioLabel(m.mes, m.anio),
    diasEnMes: getDiasEnMes(m.mes, m.anio),
    acumulado: buildAcumuladoDiarioMes(ventas, m.mes, m.anio),
  }));

  const data = [];
  for (let dia = 1; dia <= maxDias; dia += 1) {
    const row = { dia };
    seriesData.forEach((s) => {
      if (dia <= s.diasEnMes) {
        const punto = s.acumulado.find((p) => p.dia === dia);
        row[s.label] = punto ? punto.acumulado : null;
      } else {
        row[s.label] = null;
      }
    });
    data.push(row);
  }

  return { data, series: seriesData.map((s) => s.label) };
}

export function getDiaPicoMes(ventas, mes, anio) {
  const ventasMes = filterVentasPorMes(ventas, mes, anio);
  const totalesPorDia = {};

  ventasMes.forEach((v) => {
    const dia = new Date(v.fecha).getDate();
    if (!totalesPorDia[dia]) {
      totalesPorDia[dia] = { total: 0, count: 0 };
    }
    totalesPorDia[dia].total += parseFloat(v.total) || 0;
    totalesPorDia[dia].count += 1;
  });

  let mejor = { dia: null, total: 0, count: 0 };
  Object.entries(totalesPorDia).forEach(([dia, stats]) => {
    if (stats.total > mejor.total) {
      mejor = { dia: parseInt(dia, 10), total: stats.total, count: stats.count };
    }
  });

  return mejor;
}
